import type { CollectionName, SyncChange } from "@lockcal/shared";
import { api } from "@/api/client";
import {
  allRecords,
  deviceId,
  dirtyRecords,
  getMeta,
  getRecord,
  putRecord,
  putRecords,
  setMeta,
  type LocalRecord,
} from "./db";

export interface SyncResult {
  pushed: number;
  pulled: number;
  rejected: number;
  error?: string;
}

function stripInternal(record: LocalRecord): Record<string, unknown> {
  const { _dirty, _deleted, ...rest } = record;
  void _dirty;
  void _deleted;
  return rest;
}

function toChange(collection: CollectionName, record: LocalRecord): SyncChange {
  return {
    collection,
    id: record.id,
    updatedAt: record.updatedAt,
    deletedAt: record.deletedAt,
    data: stripInternal(record),
  };
}

/**
 * Two phase delta sync.
 *
 * 1. push every locally dirty record
 * 2. pull everything the server changed since our cursor
 *
 * Conflicts are resolved last-write-wins on `updatedAt`, with the device id as
 * a tie break so two devices always converge on the same value.
 */
export async function syncNow(): Promise<SyncResult> {
  let pushed = 0;
  let pulled = 0;
  let rejected = 0;

  try {
    const id = await deviceId();

    /* ---------------- push ---------------- */
    const dirty = await dirtyRecords();
    if (dirty.length > 0) {
      const changes = dirty.map(({ collection, record }) =>
        toChange(collection, record),
      );
      const response = await api.push(id, changes);

      const rejectedKeys = new Set(
        response.rejected.map((r) => `${r.collection}/${r.id}`),
      );
      rejected = response.rejected.length;

      // The server refused the change: take the server copy as the truth.
      for (const rejection of response.rejected) {
        if (rejection.reason !== "SERVER_NEWER" || !rejection.current) continue;
        const current = rejection.current as Record<string, unknown>;
        await putRecord(rejection.collection, {
          ...(current as LocalRecord),
          _dirty: 0,
          _deleted: current.deletedAt ? 1 : 0,
        });
      }

      // Everything the server accepted is now clean.
      for (const { collection, record } of dirty) {
        if (rejectedKeys.has(`${collection}/${record.id}`)) continue;
        const current = await getRecord(collection, record.id);
        if (current && current.updatedAt === record.updatedAt) {
          await putRecord(collection, { ...current, _dirty: 0 });
          pushed++;
        }
      }
    }

    /* ---------------- pull ---------------- */
    const cursor = await getMeta<number>("cursor", 0);
    const response = await api.pull(cursor);

    for (const [name, records] of Object.entries(response.collections)) {
      const collection = name as CollectionName;
      if (!records || records.length === 0) continue;

      const toApply: LocalRecord[] = [];
      for (const incoming of records as unknown as LocalRecord[]) {
        const local = await getRecord(collection, incoming.id);

        if (!local) {
          toApply.push({
            ...incoming,
            _dirty: 0,
            _deleted: incoming.deletedAt ? 1 : 0,
          });
          continue;
        }

        // A dirty local record that is newer than the server copy wins and will
        // be pushed on the next round.
        if (local._dirty === 1 && local.updatedAt >= incoming.updatedAt) continue;

        toApply.push({
          ...incoming,
          _dirty: 0,
          _deleted: incoming.deletedAt ? 1 : 0,
        });
      }

      await putRecords(collection, toApply);
      pulled += toApply.length;
    }

    await setMeta("cursor", response.rev);
    return { pushed, pulled, rejected };
  } catch (err) {
    return {
      pushed,
      pulled,
      rejected,
      error: err instanceof Error ? err.message : String(err),
    };
  }
}

/** Load every collection into memory. Personal scale, so this is cheap. */
export async function loadAll(): Promise<Record<CollectionName, LocalRecord[]>> {
  const names: CollectionName[] = [
    "calendars",
    "events",
    "reminderLists",
    "reminders",
    "habits",
    "habitCheckIns",
    "stickyNotes",
    "settings",
  ];
  const entries = await Promise.all(
    names.map(async (name) => [name, await allRecords(name)] as const),
  );
  return Object.fromEntries(entries) as Record<CollectionName, LocalRecord[]>;
}
